import { EMAIL_PATTERN } from 'constants/defaultConstants';
import { FC } from 'react';

import cleanText from 'utils/cleanText';
import normalizeEmail from 'utils/normalizeEmail';

import InputCharCounter from '../InputCharCounter';
import MandatoryField from '../MandatoryField';
import InputMessage, { MessageType } from '../InputMessage';
import IconTooltip from '../Icon/IconTooltip';

type InputTextProps = {
  id: string;
  value: string;
  label?: string;
  placeholder?: string;
  icon?: string;
  onChange: (value: string) => void; // eslint-disable-line
  onBlur?: VoidFunction; // eslint-disable-line
  required?: boolean;
  lgSize?: boolean;
  disabled?: boolean;
  emailInput?: boolean;
  marginBottom?: number;
  communicationInput?: boolean;
  onlyInput?: boolean;
  readOnly?: boolean;
  showCounter?: boolean;
  minLength?: number;
  maxLength?: number;
  additionalInfo?: string;
  additionalInfoType?: MessageType;
  tooltip?: string;
};

const DEFAULT_MAX_LENGTH = 50;

const InputText: FC<InputTextProps> = ({
  id,
  value,
  label,
  placeholder = '',
  onChange,
  onBlur,
  required = false,
  lgSize = true,
  disabled = false,
  icon,
  emailInput = false,
  marginBottom,
  communicationInput = false,
  onlyInput = false,
  readOnly = false,
  showCounter = false,
  minLength = 0,
  maxLength,
  additionalInfo,
  additionalInfoType,
  tooltip,
}) => {
  // eslint-disable-next-line
  const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const sanitizedText = cleanText(value);

    if (emailInput) {
      const normalizedEmail = normalizeEmail(sanitizedText);
      onChange(normalizedEmail);
    } else {
      onChange(sanitizedText);
    }
  };

  const inputClassName = communicationInput
    ? 'input-mail tagify'
    : `form-control ${lgSize ? 'form-control-lg' : ''}`;

  const InputComponent = (
    <input
      id={id}
      type={emailInput ? 'email' : 'text'}
      className={inputClassName}
      value={value}
      onChange={handleChangeInput}
      onBlur={onBlur}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      pattern={emailInput ? EMAIL_PATTERN : undefined}
      readOnly={readOnly}
      maxLength={maxLength}
      minLength={minLength}
    />
  );

  return (
    <>
      {communicationInput || onlyInput ? (
        InputComponent
      ) : (
        <div
          className={`form-group ${
            marginBottom !== undefined ? `mb-${marginBottom}` : ''
          }`}
        >
          {label && (
            <label className="form-label d-flex " htmlFor={id}>
              {label}{' '}
              {required && (
                <span className="ml-1">
                  <MandatoryField />
                </span>
              )}
              {tooltip && (
                <IconTooltip
                  id={`${id}-tooltip`}
                  tooltip={tooltip}
                  place="right"
                />
              )}
            </label>
          )}

          <div className="form-control-wrap">
            {icon && (
              <div className="form-icon form-icon-left">
                <em className={`icon ni ${icon}`} />
              </div>
            )}

            {InputComponent}

            {additionalInfo && (
              <InputMessage
                text={additionalInfo}
                type={additionalInfoType || MessageType.INFO}
              />
            )}

            {showCounter && (
              <InputCharCounter
                value={value}
                limit={maxLength || DEFAULT_MAX_LENGTH}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default InputText;
